/**
 * One small glyph per warning kind, so a strip of warnings reads at a
 * glance — a flood, a fire and a wind warning stop being three identical
 * amber lines.
 *
 * The kinds are the server's `event_kind` (0126): a normalized keyword
 * classification of the agencies' own free-text events, derived from the
 * 73 distinct live strings across all six connected feeds (2026-09-27).
 * Everything here is a content glyph BESIDE the agency's words, never an
 * icon-only control (`design-dashboard.md` §7.3), so each is aria-hidden
 * — the text carries the meaning for a screen reader.
 *
 * Drawn as 24-unit stroke paths in currentColor: they inherit the
 * attention amber on the strips and stay legible in both themes and in
 * greyscale (shape, not colour, is the differentiator — the same rule as
 * the map's three pin shapes).
 */

export type WarningKind =
  | 'flood'
  | 'storm'
  | 'wind'
  | 'rain'
  | 'cyclone'
  | 'tornado'
  | 'fire'
  | 'heat'
  | 'cold'
  | 'snow'
  | 'marine'
  | 'fog'
  | 'dust'
  | 'tsunami'
  | 'earthquake'
  | 'road'
  | 'rescue'
  | 'other';

const PATHS: Record<WarningKind, string[]> = {
  // Three rising water lines.
  flood: [
    'M3 16c2-2 4-2 6 0s4 2 6 0 4-2 6 0',
    'M3 11c2-2 4-2 6 0s4 2 6 0 4-2 6 0',
    'M7 6c2-2 4-2 6 0s4 2 6 0',
  ],
  // Cloud with a bolt.
  storm: [
    'M7 12a4.5 4.5 0 1 1 .8-8.9A5 5 0 0 1 17 5.5 3.8 3.8 0 0 1 17 12H7',
    'M12.5 12 10 17h3l-2.5 5',
  ],
  // Gust strokes with curled ends.
  wind: [
    'M3 8h10a2.5 2.5 0 1 0-2.4-3.2',
    'M3 12.5h15a2.5 2.5 0 1 1-2.4 3.2',
    'M3 17h7a2 2 0 1 1-1.9 2.6',
  ],
  // Cloud and three drops.
  rain: [
    'M7 12a4.5 4.5 0 1 1 .8-8.9A5 5 0 0 1 17 5.5 3.8 3.8 0 0 1 17 12H7',
    'M8 15l-1.2 3',
    'M12.5 15l-1.2 3',
    'M17 15l-1.2 3',
  ],
  // Inward spiral.
  cyclone: [
    'M12 21a9 9 0 1 1 9-9 6.5 6.5 0 0 1-6.5 6.5A4.8 4.8 0 0 1 9.7 13.7 3.4 3.4 0 0 1 13 10.3',
  ],
  // Shrinking funnel.
  tornado: ['M4 5h16', 'M6.5 9.5h11', 'M9 14h7', 'M11.5 18.5h3.5', 'M13 22h1'],
  // Flame outline.
  fire: [
    'M12 22c-4 0-7-2.8-7-6.6 0-2.9 1.8-4.7 3.3-6.5C9.7 7.2 10.9 5.2 11 2c2.6 1.8 4 4 3.5 6.7 1.1-.4 1.9-1.1 2.4-2.2C18.4 8.4 19 10.5 19 13c0 5-3 9-7 9z',
  ],
  // Sun.
  heat: [
    'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z',
    'M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3M4.6 4.6l2.1 2.1M17.3 17.3l2.1 2.1M19.4 4.6l-2.1 2.1M6.7 17.3l-2.1 2.1',
  ],
  // Thermometer, mercury low.
  cold: ['M10 14.5V5a2 2 0 1 1 4 0v9.5a4 4 0 1 1-4 0z', 'M12 17.5v-3', 'M18 4h4M20 2v4'],
  // Six-arm flake.
  snow: ['M12 2v20M4 6l16 12M20 6 4 18', 'M12 2l-2 2.5M12 2l2 2.5M12 22l-2-2.5M12 22l2-2.5'],
  // One breaking wave.
  marine: ['M3 18c1-6 5-10 10-10a7 7 0 0 1 7 7c0-3-2-5-5-5-4 0-6 3-6 8', 'M3 21h18'],
  // Layered banks.
  fog: ['M4 9h13', 'M7 13h13', 'M4 17h10'],
  // Wind with particles.
  dust: [
    'M3 9h9a2.5 2.5 0 1 0-2.4-3.2',
    'M3 14h13',
    'M17.5 14h.01M20.5 10h.01M19 18h.01M15.5 18.5h.01',
  ],
  // Tall wave over a baseline.
  tsunami: ['M3 20h18', 'M4 16C4 8 8 4 13 4c4 0 7 3 7 7 0-2.5-1.8-4.5-4.5-4.5C11 6.5 9 10 9 16'],
  // Seismograph trace.
  earthquake: ['M2 13h4l2.5-7 3.5 12 3-9 2 4h5'],
  // Converging road with centreline.
  road: ['M9 4 5 20M15 4l4 16', 'M12 6v2.5M12 12v2.5M12 18v2'],
  // Lifebuoy.
  rescue: [
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
    'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
    'M12 3v5M12 16v5M3 12h5M16 12h5',
  ],
  // Triangle with a mark.
  other: ['M12 3 2.5 20h19L12 3z', 'M12 9.5V14M12 17h.01'],
};

export function warningKind(value: string | null | undefined): WarningKind {
  return value && value in PATHS ? (value as WarningKind) : 'other';
}

/**
 * The same glyph as a string of SVG, for the map's warning pins — the engines
 * place DOM they are handed, not React. `colour` is the stroke (a CSS value;
 * the pin is ordinary DOM, so a token `var()` resolves).
 */
export function warningIconSvg(
  kind: string | null | undefined,
  size: number,
  colour: string,
): string {
  const paths = PATHS[warningKind(kind)].map((d) => `<path d="${d}"/>`).join('');
  return (
    `<svg aria-hidden="true" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" ` +
    `stroke="${colour}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`
  );
}

export function WarningKindIcon({
  kind,
  size = 16,
  className,
}: {
  kind: string | null | undefined;
  size?: number;
  className?: string;
}) {
  const k = warningKind(kind);
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {PATHS[k].map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}
